import { useState } from "react";
import { ActionButton, Dialog, DialogContent } from "@jhool-io/fe-components";
import { useParams } from "react-router-dom";
import { useGetDiagnosisCodesDisplay } from "../../../../hooks/helpers";
import { STANDARD_TESTING_NOTE_LABELS } from "../../../../utils/constants";
import { IAddendumDetails } from "../../../../modules/notes/types/notes.types";
import NoteContentItem from "../NoteContentItem/NoteContentItem";
import styles from "../../../../modules/notes/components/NoteContent/NoteContent.module.scss";
import useGetDiagnosisCodeToDisplay from "../../../../modules/notes/hooks";
import { cn, displayNameInRightFormat } from "../../../../utils/helpers";
import CheckIcon from "../../../../icons/Check";
import { useFetchSessionNote } from "../../../../hooks/queries/note";
import ViewFile from "../../../../shared-ui/ViewFile/ViewFile";
import { IViewFile } from "../../../../utils/types";

interface ViewStandardNoteTemplateTypeProps {
    noteContent: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any;
    };
    fieldAddendums: {
        [key: string]: IAddendumDetails[];
    } | null;
}

export default function ViewStandardNoteTemplateType({
    noteContent,
    fieldAddendums,
}: ViewStandardNoteTemplateTypeProps) {
    const [docInView, setDocInView] = useState<IViewFile | null>(null);

    const params = useParams();
    const noteId = params.noteId as string;
    const clientId = params.clientId as string;

    const { data } = useFetchSessionNote(
        clientId,
        noteId,
        Boolean(clientId) && Boolean(noteId)
    );

    const diagonosisCodes = useGetDiagnosisCodesDisplay(
        noteContent.diagnosis_codes
    );

    return (
        <>
            {docInView ? (
                <Dialog open={Boolean(docInView)}>
                    <DialogContent
                        title={
                            docInView?.document_name ??
                            "Note content attachment"
                        }
                        handleCloseDialog={() => {
                            setDocInView(null);
                        }}
                        showFooter={false}
                    >
                        <ViewFile
                            url={docInView.url}
                            contentType={
                                docInView?.metadata?.content_type ?? ""
                            }
                            title={
                                docInView?.document_name ??
                                "Note content attachment"
                            }
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            <NoteContentItem
                headerText={STANDARD_TESTING_NOTE_LABELS.diagnosis_codes}
                fieldId="diagnosis_codes"
                addendums={fieldAddendums?.diagnosis_codes}
                content={
                    diagonosisCodes && Array.isArray(diagonosisCodes) ? (
                        <div className={styles.checkboxes}>
                            {diagonosisCodes.map((code) => (
                                <div className={styles.checkbox} key={code}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: true,
                                        })}
                                    >
                                        <CheckIcon className="stroke-text" />
                                    </span>
                                    {code}
                                </div>
                            ))}
                        </div>
                    ) : (
                        "--"
                    )
                }
            />
            <NoteContentItem
                headerText={STANDARD_TESTING_NOTE_LABELS.secondary_diagnosis}
                fieldId="secondary_diagnosis"
                addendums={fieldAddendums?.secondary_diagnosis}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.secondary_diagnosis
                )}
            />
            <NoteContentItem
                headerText={STANDARD_TESTING_NOTE_LABELS.tertiary_diagnosis}
                fieldId="tertiary_diagnosis"
                addendums={fieldAddendums?.tertiary_diagnosis}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.tertiary_diagnosis
                )}
            />
            <NoteContentItem
                headerText={
                    STANDARD_TESTING_NOTE_LABELS.additional_diagnosis_one
                }
                fieldId="additional_diagnosis_one"
                addendums={fieldAddendums?.additional_diagnosis_one}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.additional_diagnosis_one
                )}
            />
            <NoteContentItem
                headerText={
                    STANDARD_TESTING_NOTE_LABELS.additional_diagnosis_two
                }
                fieldId="additional_diagnosis_two"
                addendums={fieldAddendums?.additional_diagnosis_two}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.additional_diagnosis_two
                )}
            />
            <NoteContentItem
                headerText={STANDARD_TESTING_NOTE_LABELS.files}
                fieldId="files"
                addendums={fieldAddendums?.files}
                content={
                    <div>
                        {data?.data?.note_content_documents &&
                        data?.data?.note_content_documents.length ? (
                            <div className="flex gap-[var(--spacing-items)] flex-wrap">
                                {data?.data?.note_content_documents.map(
                                    (doc) => (
                                        <ActionButton
                                            key={doc.note_content_document_id}
                                            variant="can-hover"
                                            size="auto"
                                            className="h-7 !text-text text-caption underline"
                                            actionTitle="View file"
                                            onClick={() => {
                                                setDocInView(doc);
                                            }}
                                        >
                                            <span>{`${displayNameInRightFormat(
                                                {
                                                    firstName:
                                                        doc.uploaded_by
                                                            .first_name,
                                                    lastName:
                                                        doc.uploaded_by
                                                            .last_name,
                                                },
                                                false
                                            )} -  ${doc.document_name}`}</span>
                                        </ActionButton>
                                    )
                                )}
                            </div>
                        ) : (
                            "--"
                        )}
                    </div>
                }
            />
            <NoteContentItem
                headerText={STANDARD_TESTING_NOTE_LABELS.summary}
                fieldId="summary"
                addendums={fieldAddendums?.summary}
                content={noteContent.summary || "--"}
            />
        </>
    );
}
